import { format } from 'date-fns'
import { useMemo } from 'react'

import { parseDateOnly } from '../../lib/formatters'
import { type BusinessOwner, updateBusinessOwner, type UpdateBusinessOwnerData } from '../../models/user'
import { applyServerErrors } from './formServerErrors'
import { type SaveResult, type SyncedForm, useSyncedForm } from './useSyncedForm'

export interface OwnerInfoValues {
  firstName: string
  lastName: string
  email: string
  dob: Date | null
  mobile: string
  maritalStatus: string
}

export type OwnerInfoField = keyof OwnerInfoValues

export interface OwnerInfoForm extends SyncedForm<OwnerInfoValues> {
  save: (orgId: string, options: { onlyChanged: boolean }) => Promise<SaveResult>
}

const ERROR_FIELDS: Record<string, OwnerInfoField> = {
  first_name: 'firstName',
  last_name: 'lastName',
  email: 'email',
  dob: 'dob',
  mobile: 'mobile',
  marital_status: 'maritalStatus',
}

export function ownerInfoValues(owner: BusinessOwner | null | undefined): OwnerInfoValues {
  return {
    firstName: owner?.firstName || '',
    lastName: owner?.lastName || '',
    email: owner?.email || '',
    dob: parseDateOnly(owner?.dob) ?? null,
    mobile: owner?.mobile || '',
    maritalStatus: owner?.maritalStatus || '',
  }
}

/** What to PATCH to `business-owner/`: the whole form, or with `changed` only those fields. */
export function ownerInfoPayload(
  values: OwnerInfoValues,
  changed?: readonly OwnerInfoField[]
): UpdateBusinessOwnerData {
  const include = (field: OwnerInfoField) => !changed || changed.includes(field)
  const payload: UpdateBusinessOwnerData = {}
  if (include('firstName')) payload.firstName = values.firstName
  if (include('lastName')) payload.lastName = values.lastName
  if (include('email')) payload.email = values.email
  if (include('dob')) payload.dob = values.dob ? format(values.dob, 'yyyy-MM-dd') : null
  if (include('mobile')) payload.mobile = values.mobile || null
  if (include('maritalStatus')) payload.maritalStatus = values.maritalStatus || null
  return payload
}

/** The owner endpoint answers refusals as `{error}`; anything else gets the generic line. */
export function ownerSaveErrorMessage(error: unknown): string {
  return (
    (error as { response?: { data?: { error?: string } } } | null | undefined)?.response?.data?.error ??
    'Failed to update owner info'
  )
}

export function useOwnerInfoForm(owner: BusinessOwner | null | undefined): OwnerInfoForm {
  const values = useMemo(() => ownerInfoValues(owner), [owner])
  const synced = useSyncedForm(values)

  async function save(orgId: string, { onlyChanged }: { onlyChanged: boolean }): Promise<SaveResult> {
    const { form, changedFields, markSaved } = synced
    form.clearErrors()
    if (onlyChanged && changedFields.length === 0) return { ok: true }
    if (!(await form.trigger())) return { ok: false }
    try {
      await updateBusinessOwner(
        orgId,
        ownerInfoPayload(form.getValues(), onlyChanged ? changedFields : undefined)
      )
      markSaved()
      return { ok: true }
    } catch (error) {
      applyServerErrors(form, error, ERROR_FIELDS)
      return { ok: false, error }
    }
  }

  return { ...synced, save }
}
